// Sprint 2026-Jun/Jul — VoltLedger invoice lifecycle.
// Invoice-Level Receivable Detail — THE single operational page for
// Accounts / Commercial / Recovery. Opened as a dynamic tab from the
// Retention Queue, Invoice Follow-up Queue, Holds/Requests tables and
// Dashboard drills. STRICTLY read-only over sales_invoices; writes go to
// the extension doc, invoice_status_events (append-only) and additive
// retention collections.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTabs } from "@/contexts/TabsContext";
import { Card } from "@/components/ui/card";
import {
  ArrowDownToLine, ArrowRight, ClipboardList, FileText, History, Landmark,
  Paperclip, Pencil, Plus, ReceiptText, RotateCw, Scale, Trash2, Workflow, X,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const inr = (v) => (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const fmtDT = (s) => (s || "").replace("T", " ").slice(0, 19) || "—";

const STATUS_TONE = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Approved: "bg-sky-50 text-sky-700 border-sky-200",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200",
  Settled: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const Badge = ({ s }) => (
  <span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[s] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{s || "—"}</span>
);

const DUE_SOURCE_LABELS = {
  manual_override: "Manual Override", invoice_due_date: "Invoice Due Date",
  ses_plus_days: "SES + Credit Days", client_credit_days: "Client Credit Days",
  missing: "Missing",
};

const TABS = [
  ["overview", "Overview", FileText],
  ["jobsheet", "Jobsheet / SES", ClipboardList],
  ["receipts", "Payments", ArrowDownToLine],
  ["retentions", "Retentions", Landmark],
  ["adjustments", "LD / CESS / Round-off", Scale],
  ["cn", "Credit Notes", FileText],
  ["status", "Status Timeline", Workflow],
  ["audit", "Audit Trail", History],
];

// Shared opener — one dynamic-tab entry point for every module that links
// to the invoice operational page (Retentions, Invoice Queue, Dashboard).
export function useOpenInvoiceDetail(parentPath) {
  const { openFormTab } = useTabs();
  return (invoiceId, invoiceNumber) => openFormTab({
    path: `/receivables/invoice/${invoiceId}`,
    label: `SI ${invoiceNumber || ""}`.trim(),
    icon: ReceiptText,
    render: () => <InvoiceReceivableDetail invoiceId={invoiceId} />,
    parent: parentPath,
  });
}

export default function InvoiceReceivableDetail({ invoiceId }) {
  const { user, hasPerm } = useAuth();
  const can = (action) => user?.role === "admin" || hasPerm("invoice_queue", action);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [showRequest, setShowRequest] = useState(false);
  const [showStatus, setShowStatus] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/sales-invoices/${invoiceId}/receivable-detail`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoice detail");
    } finally { setLoading(false); }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="py-16 text-center text-sm text-slate-400" data-testid="invoice-detail-loading">Loading invoice detail…</div>;
  if (!data) return <div className="py-16 text-center text-sm text-slate-400" data-testid="invoice-detail-error">Invoice detail unavailable.</div>;

  const { invoice, summary } = data;
  return (
    <div data-testid="invoice-receivable-detail">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-orange-600">Receivables · Invoice Detail</div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2 flex-wrap" data-testid="detail-invoice-number">
            {invoice.invoice_number}
            <Badge s={invoice.status} />
            {summary.receivable_status
              ? <span data-testid="detail-current-status" className="border border-sky-300 bg-sky-50 text-sky-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{summary.receivable_status}</span>
              : <span data-testid="detail-current-status" className="border border-slate-200 bg-slate-50 text-slate-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Status Not Set</span>}
          </h1>
          <div className="text-xs text-slate-500 mt-0.5">
            {invoice.client_name} · {invoice.project_name} · {invoice.branch_name || "—"} · Dated {invoice.invoice_date || "—"}
          </div>
        </div>
        <div className="flex gap-2">
          {can("update_status") && (
            <button data-testid="btn-update-status" onClick={() => setShowStatus(true)}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
              <Pencil size={12} /> Update Status
            </button>
          )}
          <button data-testid="detail-refresh" onClick={load}
            className="border border-slate-300 px-3 py-2 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
            <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {[["Net Payable", summary.net_payable, "border-slate-300 bg-slate-100 text-slate-900", "kpi-net-payable"],
          ["Received", summary.total_paid, "border-emerald-200 bg-emerald-50 text-emerald-900", "kpi-received"],
          ["Outstanding", summary.outstanding, "border-rose-200 bg-rose-50 text-rose-900", "kpi-outstanding"],
          ["Retention Held", summary.pending_retention, "border-amber-200 bg-amber-50 text-amber-900", "kpi-retention-held"],
          ["Net Collectible", summary.net_collectible, "border-sky-200 bg-sky-50 text-sky-900", "kpi-net-collectible"]].map(([l, v, tone, tid]) => (
          <div key={l} className={`border px-3 py-2.5 ${tone}`} data-testid={tid}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{l}</div>
            <div className="text-sm font-bold mt-0.5">₹{inr(v)}</div>
          </div>
        ))}
      </div>

      <Card>
        <div className="px-3 pt-2 border-b border-slate-100 flex flex-wrap gap-1 overflow-x-auto">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} data-testid={`detail-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 flex items-center gap-1.5 whitespace-nowrap ${tab === k ? "border-orange-500 text-orange-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              <Icon size={12} /> {l}
            </button>
          ))}
        </div>
        <div className="p-4">
          {tab === "overview" && <OverviewTab data={data} />}
          {tab === "jobsheet" && (
            <JobsheetTab data={data} onSaved={load}
              canEdit={can("update_jobsheet")} canOverride={can("override_due_date")} />
          )}
          {tab === "receipts" && <ReceiptsTab receipts={data.receipts} />}
          {tab === "retentions" && (
            <RetentionsTab data={data} onRaise={() => setShowRequest(true)} />
          )}
          {tab === "adjustments" && <AdjustmentsTab data={data} />}
          {tab === "cn" && <CreditNotesTab data={data} />}
          {tab === "status" && <StatusTimelineTab data={data} />}
          {tab === "audit" && <AuditTab data={data} />}
        </div>
      </Card>

      {showRequest && (
        <RaiseRequestDialog data={data} onClose={() => setShowRequest(false)}
          onDone={() => { setShowRequest(false); load(); }} />
      )}
      {showStatus && (
        <StatusUpdateDialog data={data} onClose={() => setShowStatus(false)}
          onDone={() => { setShowStatus(false); load(); }} />
      )}
    </div>
  );
}

/* ── shared bits ─────────────────────────────────────────────────────── */
const Row = ({ label, value, bold, tone, testid }) => (
  <div className={`flex justify-between border-b border-slate-50 py-1.5 text-xs ${bold ? "font-bold" : ""}`} data-testid={testid}>
    <span className="text-slate-500">{label}</span>
    <span className={`tabular-nums ${tone || "text-slate-800"}`}>{value}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{children}</div>
);

const Empty = ({ children }) => (
  <div className="px-3 py-8 text-center text-xs text-slate-400">{children}</div>
);

function MiniTable({ head, children, testid }) {
  return (
    <div className="overflow-x-auto border border-slate-100">
      <table className="w-full text-xs" data-testid={testid}>
        <thead>
          <tr className="bg-slate-100 text-slate-600">
            {head.map((h) => <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const AttLinks = ({ atts }) => (
  (atts || []).length === 0 ? <>—</> : (atts || []).map((a, i) => (
    <a key={i} href={`${BASE}${a.url}`} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 text-sky-700 hover:underline mr-2" title={a.filename}>
      <Paperclip size={11} />{(a.filename || "doc").slice(0, 18)}
    </a>
  ))
);

// Shared attachment upload control (uses the existing /api/upload flow).
function AttachmentPicker({ atts, setAtts, testidPrefix, max = 5 }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  async function addFiles(files) {
    if (!files?.length) return;
    if (atts.length + files.length > max) return toast.error(`Maximum ${max} attachments`);
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd);
        setAtts((prev) => [...prev, { filename: r.data.filename, url: r.data.url, size: r.data.size }]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  return (
    <div>
      <input ref={fileRef} data-testid={`${testidPrefix}-file-input`} type="file" multiple
        onChange={(e) => addFiles(Array.from(e.target.files || []))}
        className="text-xs" disabled={uploading || atts.length >= max} />
      {uploading && <div className="text-xs text-slate-400 mt-1">Uploading…</div>}
      <div className="mt-2 space-y-1">
        {atts.map((a, i) => (
          <div key={i} className="flex items-center justify-between border border-slate-200 px-2.5 py-1.5 text-xs" data-testid={`${testidPrefix}-attachment-${i}`}>
            <span className="flex items-center gap-1.5 truncate"><Paperclip size={11} className="text-slate-400" />{a.filename}</span>
            <button onClick={() => setAtts((prev) => prev.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="mb-3">
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);

function Modal({ title, children, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} className={`bg-white w-full ${wide ? "max-w-2xl" : "max-w-lg"} shadow-2xl border border-slate-200 max-h-[92vh] overflow-auto`}>
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-bold text-slate-800">{title}</div>
          <button data-testid={`${testid}-close`} onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ── Tab 1: Overview ─────────────────────────────────────────────────── */
function OverviewTab({ data }) {
  const { invoice, summary } = data;
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="overview-tab">
      <div>
        <SectionTitle>Invoice Money Trail</SectionTitle>
        <Row label="Base Amount" value={`₹${inr(summary.base_amount)}`} />
        <Row label="GST" value={`₹${inr(summary.gst_amount)}`} />
        <Row label="Invoice Total" value={`₹${inr(summary.net_amount)}`} bold />
        <Row label="TDS (invoice)" value={`− ₹${inr(summary.tds_amount)}`} tone="text-rose-700" />
        <Row label="Special Deductions" value={`− ₹${inr(summary.deductions_total)}`} tone="text-rose-700" />
        <Row label="CESS" value={`− ₹${inr(summary.cess_amount)}`} tone="text-rose-700" />
        <Row label="LD" value={`− ₹${inr(summary.ld_amount)}`} tone="text-rose-700" />
        <Row label="Round-off" value={`− ₹${inr(summary.rounding_deduction)}`} tone="text-rose-700" />
        <Row label="Net Payable" value={`₹${inr(summary.net_payable)}`} bold testid="ov-net-payable" />
        <Row label="Debit Notes" value={`+ ₹${inr(summary.debit_note_total)}`} tone="text-emerald-700" />
        <Row label="Cash / Bank Received (incl. receipt TDS)" value={`− ₹${inr(summary.total_paid)}`} tone="text-emerald-700" />
        <Row label="Credit Notes (legacy + allocations)" value={`− ₹${inr(summary.credit_note_total)}`} tone="text-rose-700" />
        <Row label="Retention Released / Settled" value={`− ₹${inr(summary.total_retention_paid)}`} tone="text-rose-700" />
        <Row label="Total Outstanding" value={`₹${inr(summary.outstanding)}`} bold testid="ov-outstanding" />
        <Row label="Held Retention (inside outstanding)" value={`₹${inr(summary.pending_retention)}`} tone="text-amber-700" />
        <Row label="Net Collectible (Excl. Held Retention)" value={`₹${inr(summary.net_collectible)}`} bold tone="text-sky-800" testid="ov-net-collectible" />
      </div>
      <div>
        <SectionTitle>Invoice & Due Info</SectionTitle>
        <Row label="Invoice Date" value={invoice.invoice_date || "—"} />
        <Row label="Service Period" value={invoice.service_period || "—"} />
        <Row label="RC Number" value={invoice.rc_number || "—"} />
        <Row label="Sub Vendor" value={invoice.sub_vendor_name || "—"} />
        <Row label="Payment Status" value={invoice.payment_status || "—"} />
        <Row label="Follow-up Status" value={summary.receivable_status || "Not Set"} bold testid="ov-followup-status" />
        <Row label="Due Date" value={summary.payment_due_date || "Due Date Missing"} bold testid="ov-due-date" />
        <Row label="Due Date Source" value={DUE_SOURCE_LABELS[summary.payment_due_source] || summary.payment_due_source} />
        <Row label="FY" value={summary.fy || "—"} />
        <Row label="Created" value={`${fmtDT(invoice.created_at)} · ${invoice.created_by || "—"}`} />
        <Row label="Approved" value={`${fmtDT(invoice.approved_at)} · ${invoice.approved_by || "—"}`} />
        <div className="mt-4">
          <SectionTitle>Pending Retention by Head</SectionTitle>
          {Object.entries(summary.pending_by_head || {}).filter(([, v]) => v > 0).length === 0 && (
            <div className="text-xs text-slate-400">No retention pending.</div>
          )}
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(summary.pending_by_head || {}).filter(([, v]) => v > 0).map(([h, v]) => (
              <span key={h} className="border border-amber-200 bg-amber-50 text-amber-800 px-2 py-1 text-[10px] font-bold">
                {h}: ₹{inr(v)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tab 2: Jobsheet / SES (editable — permission gated) ─────────────── */
const JOBSHEET_FIELDS = [
  ["jobsheet_number", "Jobsheet Number", "text"],
  ["jobsheet_upload_date", "Jobsheet Upload Date", "date"],
  ["invoice_upload_date", "Invoice Upload Date", "date"],
  ["ses_approval_date", "SES Approval Date", "date"],
  ["jobsheet_final_approval_date", "Jobsheet Final Approval Date", "date"],
];

function JobsheetTab({ data, canEdit, canOverride, onSaved }) {
  const ext = data.ext || {};
  const js = ext.jobsheet || {};
  const { invoice, summary } = data;
  const [form, setForm] = useState(() =>
    Object.fromEntries(JOBSHEET_FIELDS.map(([k]) => [k, js[k] || ""])));
  const [saving, setSaving] = useState(false);
  const [ovrDate, setOvrDate] = useState(ext.payment_due_date_override || "");
  const [ovrReason, setOvrReason] = useState("");
  const [ovrSaving, setOvrSaving] = useState(false);
  useEffect(() => {
    setForm(Object.fromEntries(JOBSHEET_FIELDS.map(([k]) => [k, (data.ext?.jobsheet || {})[k] || ""])));
    setOvrDate(data.ext?.payment_due_date_override || "");
  }, [data]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/sales-invoices/${invoice.id}/jobsheet`, { jobsheet: form });
      if (Object.keys(r.data.changes || {}).length === 0) toast.info("No changes to save");
      else toast.success(`Jobsheet updated — due date now ${r.data.due_date || "missing"}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function applyOverride(clear) {
    if (!ovrReason.trim()) return toast.error("A reason is required for due-date changes");
    if (!clear && !ovrDate) return toast.error("Pick an override date");
    setOvrSaving(true);
    try {
      const r = await api.put(`/sales-invoices/${invoice.id}/due-date-override`,
        clear ? { clear: true, reason: ovrReason } : { new_due_date: ovrDate, reason: ovrReason });
      toast.success(`Due date is now ${r.data.due_date || "missing"} (${DUE_SOURCE_LABELS[r.data.due_source] || r.data.due_source})`);
      setOvrReason("");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Override failed"); }
    finally { setOvrSaving(false); }
  }

  const history = ext.jobsheet_history || [];
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="jobsheet-tab">
      <div>
        <SectionTitle>Jobsheet / SES Milestones {canEdit ? "" : "(read-only)"}</SectionTitle>
        {JOBSHEET_FIELDS.map(([k, l, type]) => (
          <Field key={k} label={l}>
            {canEdit ? (
              <input data-testid={`js-input-${k}`} type={type} value={form[k] || ""}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} />
            ) : (
              <div className="text-xs text-slate-700 border border-slate-100 px-2.5 py-2 bg-slate-50" data-testid={`js-${k}`}>{js[k] || "—"}</div>
            )}
          </Field>
        ))}
        {canEdit && (
          <button data-testid="js-save" disabled={saving} onClick={save}
            className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white py-2.5 text-xs uppercase tracking-wider font-bold disabled:opacity-50">
            {saving ? "Saving…" : "Save Jobsheet / SES"}
          </button>
        )}
        <div className="mt-3 text-[11px] text-slate-400">
          Payment Due Date auto-recalculates as SES Approval Date + company-configured credit days (Company Setup) the moment SES changes — unless a manual override is set.
        </div>
      </div>
      <div>
        <SectionTitle>Payment Due Date</SectionTitle>
        <Row label="Current Due Date" value={summary.payment_due_date || "Due Date Missing"} bold testid="js-due-date" />
        <Row label="Source" value={DUE_SOURCE_LABELS[summary.payment_due_source] || summary.payment_due_source} testid="js-due-source" />
        <Row label="Manual Override" value={ext.payment_due_date_override || "—"} />
        {canOverride && (
          <div className="mt-3 border border-amber-200 bg-amber-50/50 p-3">
            <SectionTitle>Override Due Date (permission-controlled)</SectionTitle>
            <Field label="Override Date">
              <input data-testid="ovr-date" type="date" value={ovrDate} onChange={(e) => setOvrDate(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Reason (required — audited)">
              <input data-testid="ovr-reason" value={ovrReason} onChange={(e) => setOvrReason(e.target.value)} className={inputCls} />
            </Field>
            <div className="flex gap-2">
              <button data-testid="ovr-apply" disabled={ovrSaving} onClick={() => applyOverride(false)}
                className="flex-1 bg-amber-700 hover:bg-amber-800 text-white py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50">Apply Override</button>
              {ext.payment_due_date_override && (
                <button data-testid="ovr-clear" disabled={ovrSaving} onClick={() => applyOverride(true)}
                  className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Clear</button>
              )}
            </div>
          </div>
        )}
        <div className="mt-4">
          <SectionTitle>Jobsheet Change History (append-only)</SectionTitle>
          <div className="max-h-56 overflow-auto space-y-1.5" data-testid="jobsheet-history">
            {history.length === 0 && <div className="text-xs text-slate-400">No jobsheet changes recorded.</div>}
            {[...history].reverse().map((e, i) => (
              <div key={i} className="border border-slate-200 px-3 py-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-600 font-semibold">{e.by}</span>
                  <span className="text-slate-400">{fmtDT(e.at)}</span>
                </div>
                {Object.entries(e.changes || {}).map(([f, c]) => (
                  <div key={f} className="text-slate-500 mt-0.5">
                    {f.replace(/_/g, " ")}: <span className="line-through">{c.from || "—"}</span> → <b>{c.to || "—"}</b>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tab 3: Payments ─────────────────────────────────────────────────── */
function ReceiptsTab({ receipts }) {
  const total = receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const tds = receipts.reduce((s, r) => s + (Number(r.tds_deducted) || 0), 0);
  return (
    <div data-testid="receipts-tab">
      <div className="flex justify-between items-center mb-2">
        <SectionTitle>Payment Receipts against this invoice</SectionTitle>
        <div className="text-xs text-slate-500">Received <b>₹{inr(total)}</b> · Receipt TDS <b>₹{inr(tds)}</b></div>
      </div>
      <MiniTable testid="receipts-table" head={["Date", "Receipt No", "Voucher", "Mode", "Account", "Reference", "Amount", "TDS", "Narration"]}>
        {receipts.length === 0 && <tr><td colSpan={9}><Empty>No payment receipts recorded.</Empty></td></tr>}
        {receipts.map((r) => (
          <tr key={r.id} className="border-b border-slate-50 hover:bg-orange-50/30">
            <td className="px-2.5 py-2 whitespace-nowrap">{(r.payment_date || "").slice(0, 10)}</td>
            <td className="px-2.5 py-2 whitespace-nowrap font-semibold">{r.payment_number || "—"}</td>
            <td className="px-2.5 py-2 whitespace-nowrap">{r.receipt_voucher_number || "—"}</td>
            <td className="px-2.5 py-2 whitespace-nowrap">{r.mode || "—"}</td>
            <td className="px-2.5 py-2 whitespace-nowrap">{r.account || "—"}</td>
            <td className="px-2.5 py-2 whitespace-nowrap">{r.reference_no || "—"}</td>
            <td className="px-2.5 py-2 text-right tabular-nums font-semibold">₹{inr(r.amount)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums text-rose-700">₹{inr(r.tds_deducted)}</td>
            <td className="px-2.5 py-2 max-w-[280px] truncate" title={r.narration}>{r.narration || "—"}</td>
          </tr>
        ))}
      </MiniTable>
    </div>
  );
}

/* ── Tab 4: Retentions (+ Raise Release Request) ─────────────────────── */
function RetentionsTab({ data, onRaise }) {
  const holds = data.retentions || [];
  const reqs = data.requests || [];
  const pendingTotal = data.summary.pending_retention || 0;
  return (
    <div data-testid="retentions-tab">
      <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(data.summary.pending_by_head || {}).filter(([, v]) => v > 0).map(([h, v]) => (
            <span key={h} className="border border-amber-200 bg-amber-50 text-amber-800 px-2 py-1 text-[10px] font-bold">{h}: ₹{inr(v)}</span>
          ))}
          {pendingTotal <= 0 && <span className="text-xs text-slate-400">No retention pending on this invoice.</span>}
        </div>
        {pendingTotal > 0 && (
          <button data-testid="btn-raise-release-request" onClick={onRaise}
            className="bg-sky-700 hover:bg-sky-800 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5">
            <Plus size={13} /> Raise Release Request
          </button>
        )}
      </div>

      <SectionTitle>Retention Holds</SectionTitle>
      <MiniTable testid="detail-holds-table" head={["Head", "Held", "Released", "Balance", "Status", "Date", "Source", "Remarks"]}>
        {holds.length === 0 && <tr><td colSpan={8}><Empty>No retention holds.</Empty></td></tr>}
        {holds.map((r) => (
          <tr key={r.id} className="border-b border-slate-50">
            <td className="px-2.5 py-2 whitespace-nowrap">{r.head}</td>
            <td className="px-2.5 py-2 text-right tabular-nums">₹{inr(r.amount)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums text-emerald-700">₹{inr(r.paid_amount)}</td>
            <td className="px-2.5 py-2 text-right tabular-nums font-bold">₹{inr((r.amount || 0) - (r.paid_amount || 0))}</td>
            <td className="px-2.5 py-2"><Badge s={r.status} /></td>
            <td className="px-2.5 py-2 whitespace-nowrap">{r.retention_date}</td>
            <td className="px-2.5 py-2 whitespace-nowrap text-slate-500">{r.source || "manual"}</td>
            <td className="px-2.5 py-2 max-w-[220px] truncate" title={r.remarks}>{r.remarks || "—"}</td>
          </tr>
        ))}
      </MiniTable>

      <div className="mt-5">
        <SectionTitle>Release Requests</SectionTitle>
        <MiniTable testid="detail-requests-table" head={["Head", "Requested", "Settled", "Status", "Req No · Date", "Requested By", "Decided By", "Expected Release", "Docs", "Remarks"]}>
          {reqs.length === 0 && <tr><td colSpan={10}><Empty>No release requests raised yet.</Empty></td></tr>}
          {reqs.map((r) => (
            <tr key={r.id} className="border-b border-slate-50" data-testid={`detail-request-row-${r.id}`}>
              <td className="px-2.5 py-2 whitespace-nowrap">{r.head}</td>
              <td className="px-2.5 py-2 text-right tabular-nums">₹{inr(r.amount)}</td>
              <td className="px-2.5 py-2 text-right tabular-nums text-emerald-700">₹{inr(r.settled_amount)}</td>
              <td className="px-2.5 py-2"><Badge s={r.status} /></td>
              <td className="px-2.5 py-2 whitespace-nowrap">
                {r.retention_request_no || "—"}{r.retention_request_date ? ` · ${r.retention_request_date}` : ""}
              </td>
              <td className="px-2.5 py-2 whitespace-nowrap text-slate-500">{r.requested_by}</td>
              <td className="px-2.5 py-2 whitespace-nowrap text-slate-500">{r.decided_by || "—"}</td>
              <td className="px-2.5 py-2 whitespace-nowrap">{r.expected_release_date || "—"}</td>
              <td className="px-2.5 py-2 whitespace-nowrap"><AttLinks atts={r.attachments} /></td>
              <td className="px-2.5 py-2 max-w-[200px] truncate" title={r.remarks}>{r.remarks || "—"}</td>
            </tr>
          ))}
        </MiniTable>
      </div>
      <div className="mt-3 text-[11px] text-slate-400">
        Approvals / rejections and FIFO releases are performed from the Retentions (Receivable) module. Releases never create Payment Receipt entries.
      </div>
    </div>
  );
}

/* ── Tab 5: LD / CESS / Round-off ────────────────────────────────────── */
function AdjustmentsTab({ data }) {
  const { ext, invoice, summary } = data;
  const deds = (invoice.special_deductions || []).filter((d) => d.enabled !== false);
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="adjustments-tab">
      <div>
        <SectionTitle>Extension Adjustments (never mutate the SI)</SectionTitle>
        <Row label="CESS" value={`₹${inr(ext.cess_amount)}`} testid="adj-cess" />
        <Row label="LD (Liquidated Damages)" value={`₹${inr(ext.ld_amount)}`} testid="adj-ld" />
        <Row label="Round-off (±)" value={`₹${inr(ext.rounding_deduction)}`} testid="adj-roundoff" />
        <Row label="Total Adjustment Impact" bold tone="text-rose-700"
          value={`− ₹${inr((Number(ext.cess_amount) || 0) + (Number(ext.ld_amount) || 0) + (Number(ext.rounding_deduction) || 0))}`} />
        <Row label="Last Updated" value={ext.updated_at ? `${fmtDT(ext.updated_at)} · ${ext.updated_by || "—"}` : "—"} />
        <div className="mt-3 text-[11px] text-slate-400">
          Stored in the receivables extension (target-balance semantics) — the original invoice values above stay untouched. Values are entered from Bulk Receipts.
        </div>
      </div>
      <div>
        <SectionTitle>Invoice-Level TDS & Special Deductions</SectionTitle>
        <Row label="TDS Mode" value={{ invoice_deducted: "Deducted on Invoice", receipt_level: "Receipt-level TDS", not_applicable: "Not Applicable" }[summary.tds_mode] || summary.tds_mode} />
        <Row label="TDS on Invoice" value={`₹${inr(summary.tds_amount)}`} />
        <Row label="TDS recorded on Receipts" value={`₹${inr(summary.receipt_tds_recorded)}`} />
        <Row label="Suggested TDS (next receipt)" value={`₹${inr(summary.suggested_tds)}`} />
        <div className="mt-3">
          <SectionTitle>Special Deductions (on invoice)</SectionTitle>
          {deds.length === 0 && <div className="text-xs text-slate-400">None.</div>}
          {deds.map((d, i) => <Row key={i} label={d.label || d.name || `Deduction ${i + 1}`} value={`− ₹${inr(d.amount)}`} tone="text-rose-700" />)}
        </div>
      </div>
    </div>
  );
}

/* ── Tab 6: Credit Notes ─────────────────────────────────────────────── */
function CreditNotesTab({ data }) {
  const allocs = data.cn_allocations || [];
  const cns = data.legacy_credit_notes || [];
  const dns = data.debit_notes || [];
  return (
    <div className="space-y-5" data-testid="cn-tab">
      <div>
        <SectionTitle>Formal CN Allocations (partial, additive engine)</SectionTitle>
        <MiniTable testid="cn-alloc-table" head={["CN Number", "CN Date", "Allocated", "Status", "Allocation Date", "By", "Remarks"]}>
          {allocs.length === 0 && <tr><td colSpan={7}><Empty>No CN allocations against this invoice.</Empty></td></tr>}
          {allocs.map((a) => (
            <tr key={a.id} className="border-b border-slate-50">
              <td className="px-2.5 py-2 font-semibold whitespace-nowrap">{a.cn_number}</td>
              <td className="px-2.5 py-2 whitespace-nowrap">{a.cn_date || "—"}</td>
              <td className="px-2.5 py-2 text-right tabular-nums font-semibold">₹{inr(a.amount)}</td>
              <td className="px-2.5 py-2"><Badge s={a.status} /></td>
              <td className="px-2.5 py-2 whitespace-nowrap">{(a.allocation_date || "").slice(0, 10) || "—"}</td>
              <td className="px-2.5 py-2 whitespace-nowrap text-slate-500">{a.created_by || "—"}</td>
              <td className="px-2.5 py-2 max-w-[200px] truncate" title={a.remarks}>{a.remarks || "—"}</td>
            </tr>
          ))}
        </MiniTable>
      </div>
      <div>
        <SectionTitle>Legacy Credit Notes (full-amount, linked by reference)</SectionTitle>
        <MiniTable testid="legacy-cn-table" head={["Note Number", "Date", "Amount", "Status", "Reason"]}>
          {cns.length === 0 && <tr><td colSpan={5}><Empty>No legacy credit notes.</Empty></td></tr>}
          {cns.map((c) => (
            <tr key={c.id} className="border-b border-slate-50">
              <td className="px-2.5 py-2 font-semibold whitespace-nowrap">{c.note_number}</td>
              <td className="px-2.5 py-2 whitespace-nowrap">{c.note_date || "—"}</td>
              <td className="px-2.5 py-2 text-right tabular-nums">₹{inr(c.total_amount)}</td>
              <td className="px-2.5 py-2"><Badge s={c.status} /></td>
              <td className="px-2.5 py-2">{c.reason || "—"}</td>
            </tr>
          ))}
        </MiniTable>
      </div>
      <div>
        <SectionTitle>Debit Notes (add to outstanding)</SectionTitle>
        <MiniTable testid="dn-table" head={["Note Number", "Date", "Amount", "Status", "Reason"]}>
          {dns.length === 0 && <tr><td colSpan={5}><Empty>No debit notes.</Empty></td></tr>}
          {dns.map((c) => (
            <tr key={c.id} className="border-b border-slate-50">
              <td className="px-2.5 py-2 font-semibold whitespace-nowrap">{c.note_number}</td>
              <td className="px-2.5 py-2 whitespace-nowrap">{c.note_date || "—"}</td>
              <td className="px-2.5 py-2 text-right tabular-nums">₹{inr(c.total_amount)}</td>
              <td className="px-2.5 py-2"><Badge s={c.status} /></td>
              <td className="px-2.5 py-2">{c.reason || "—"}</td>
            </tr>
          ))}
        </MiniTable>
      </div>
    </div>
  );
}

/* ── Tab 7: Status Timeline (immutable) ──────────────────────────────── */
function StatusTimelineTab({ data }) {
  const rows = data.status_history || [];
  return (
    <div data-testid="status-timeline-tab">
      <SectionTitle>Invoice Status Timeline — append-only, never overwritten</SectionTitle>
      <div className="max-h-[520px] overflow-auto space-y-2">
        {rows.length === 0 && <Empty>No status changes recorded yet — use “Update Status”.</Empty>}
        {rows.map((e) => (
          <div key={e.id} className="border border-slate-200 px-3 py-2.5 text-xs" data-testid={`status-event-${e.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="border border-slate-200 bg-slate-50 text-slate-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">{e.prev_status || "Not Set"}</span>
                <ArrowRight size={11} className="text-slate-400" />
                <span className="border border-sky-300 bg-sky-50 text-sky-800 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">{e.new_status}</span>
              </div>
              <span className="text-slate-400">{fmtDT(e.updated_at)}</span>
            </div>
            <div className="text-slate-600 mt-1">by <b>{e.updated_by}</b> · source: {e.source || "—"} · audit ref: <span className="text-slate-400">{(e.audit_ref || "").slice(0, 8)}</span></div>
            {e.remarks && <div className="text-slate-500 italic mt-0.5">“{e.remarks}”</div>}
            {(e.attachments || []).length > 0 && <div className="mt-1"><AttLinks atts={e.attachments} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Tab 8: Audit Trail ──────────────────────────────────────────────── */
function AuditTab({ data }) {
  const events = useMemo(() => {
    const out = [];
    (data.audit || []).forEach((a) => out.push({
      at: a.at, by: a.actor, action: a.action,
      note: a.summary ? Object.entries(a.summary)
        .filter(([k]) => !["fills", "settled_requests", "changes"].includes(k))
        .map(([k, v]) => `${k}: ${typeof v === "number" ? inr(v) : v}`).join(" · ") : "",
    }));
    (data.retentions || []).forEach((h) => (h.history || []).forEach((e) => out.push({
      at: e.at, by: e.by, action: `hold · ${e.action} (${h.head})`,
      note: [e.applied !== undefined && `applied ₹${inr(e.applied)}`, e.amount !== undefined && `₹${inr(e.amount)}`, e.note].filter(Boolean).join(" · "),
    })));
    (data.requests || []).forEach((r) => (r.history || []).forEach((e) => out.push({
      at: e.at, by: e.by, action: `request · ${e.action} (${r.head})`,
      note: [e.applied !== undefined && `applied ₹${inr(e.applied)}`, e.amount !== undefined && `₹${inr(e.amount)}`, e.note].filter(Boolean).join(" · "),
    })));
    return out.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [data]);
  return (
    <div className="max-h-[520px] overflow-auto space-y-2" data-testid="audit-tab">
      {events.length === 0 && <Empty>No audit events for this invoice.</Empty>}
      {events.map((e, i) => (
        <div key={i} className="border border-slate-200 px-3 py-2 text-xs">
          <div className="flex justify-between">
            <span className="font-bold uppercase tracking-wider text-[10px] text-orange-600">{e.action}</span>
            <span className="text-slate-400">{fmtDT(e.at)}</span>
          </div>
          <div className="text-slate-600 mt-0.5">by {e.by || "—"}</div>
          {e.note && <div className="text-slate-500 mt-0.5 break-all">{e.note}</div>}
        </div>
      ))}
    </div>
  );
}

/* ── Update Status dialog (shared status engine, append-only) ────────── */
function StatusUpdateDialog({ data, onClose, onDone }) {
  const [options, setOptions] = useState([]);
  const [remarkOpts, setRemarkOpts] = useState([]);
  const [status, setStatus] = useState(data.summary.receivable_status || "");
  const [remarkSel, setRemarkSel] = useState("");
  const [remarks, setRemarks] = useState("");
  const [atts, setAtts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/receivables/meta").then((r) =>
      setOptions((r.data.status_options || []).filter((o) => o.active !== false))).catch(() => {});
    api.get("/receivable-status-remarks").then((r) =>
      setRemarkOpts((r.data.rows || []).filter((o) => o.active !== false))).catch(() => {});
  }, []);

  async function save() {
    if (!status) return toast.error("Select a status");
    const finalRemarks = [remarkSel, remarks.trim()].filter(Boolean).join(" — ");
    setSaving(true);
    try {
      await api.post(`/sales-invoices/${data.invoice.id}/status`, {
        status, remarks: finalRemarks, attachments: atts, source: "invoice_detail",
      });
      toast.success(`Status updated to “${status}”`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status update failed"); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={`Update Status — ${data.invoice.invoice_number}`} onClose={onClose} testid="status-update-dialog">
      <div className="mb-3 border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        Current: <b>{data.summary.receivable_status || "Not Set"}</b>. Every change writes an immutable timeline entry — history is never overwritten.
      </div>
      <Field label="New Status (from Status Master)">
        <select data-testid="su-status" value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="">— Select status —</option>
          {options.map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Predefined Remark (optional)">
        <select data-testid="su-remark-select" value={remarkSel} onChange={(e) => setRemarkSel(e.target.value)} className={inputCls}>
          <option value="">— None —</option>
          {remarkOpts.map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Additional Remarks (free text)">
        <input data-testid="su-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Attachment (optional, max 5)">
        <AttachmentPicker atts={atts} setAtts={setAtts} testidPrefix="su" />
      </Field>
      <button data-testid="su-submit" disabled={saving} onClick={save}
        className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white py-2.5 text-xs uppercase tracking-wider font-bold disabled:opacity-50">
        {saving ? "Updating…" : "Update Status"}
      </button>
    </Modal>
  );
}

/* ── Raise Release Request dialog (with document uploads) ────────────── */
function RaiseRequestDialog({ data, onClose, onDone }) {
  const openByHead = useMemo(() => {
    const m = {};
    (data.requests || []).forEach((r) => {
      if (["Pending", "Approved"].includes(r.status)) {
        m[r.head] = (m[r.head] || 0) + ((Number(r.amount) || 0) - (Number(r.settled_amount) || 0));
      }
    });
    return m;
  }, [data]);
  const headOptions = Object.entries(data.summary.pending_by_head || {})
    .map(([h, pending]) => ({ head: h, available: Math.max((pending || 0) - (openByHead[h] || 0), 0) }))
    .filter((o) => o.available > 0.005);

  const [head, setHead] = useState(headOptions[0]?.head || "");
  const [amount, setAmount] = useState("");
  const [reqNo, setReqNo] = useState("");
  const [reqDate, setReqDate] = useState("");
  const [expected, setExpected] = useState("");
  const [remarks, setRemarks] = useState("");
  const [atts, setAtts] = useState([]);
  const [saving, setSaving] = useState(false);

  const available = headOptions.find((o) => o.head === head)?.available || 0;

  async function save() {
    if (!head) return toast.error("Select a retention head");
    if (!Number(amount)) return toast.error("Enter a request amount");
    setSaving(true);
    try {
      await api.post(`/sales-invoices/${data.invoice.id}/retention-requests`, {
        head, amount: Number(amount), remarks,
        retention_request_no: reqNo.trim(),
        retention_request_date: reqDate || null,
        expected_release_date: expected || null, attachments: atts,
      });
      toast.success("Release request submitted");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={`Raise Release Request — ${data.invoice.invoice_number}`} onClose={onClose} testid="raise-request-dialog">
      <Field label="Retention Head">
        <select data-testid="rr-head" value={head} onChange={(e) => setHead(e.target.value)} className={inputCls}>
          {headOptions.map((o) => <option key={o.head} value={o.head}>{`${o.head} — available ₹${inr(o.available)}`}</option>)}
        </select>
        {headOptions.length === 0 && <div className="text-xs text-rose-600 mt-1">No head has retention available to request (pending − already requested).</div>}
      </Field>
      <Field label={`Requested Amount (₹) — max ₹${inr(available)}`}>
        <input data-testid="rr-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Retention Request Number">
          <input data-testid="rr-request-no" value={reqNo} onChange={(e) => setReqNo(e.target.value)}
            placeholder="e.g. RTN-REQ/001" className={inputCls} />
        </Field>
        <Field label="Retention Request Date">
          <input data-testid="rr-request-date" type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} className={inputCls} />
        </Field>
      </div>
      <Field label="Expected Release Date (optional)">
        <input data-testid="rr-expected" type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Remarks">
        <input data-testid="rr-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Supporting Documents (max 5)">
        <AttachmentPicker atts={atts} setAtts={setAtts} testidPrefix="rr" />
      </Field>
      <button data-testid="rr-submit" disabled={saving || headOptions.length === 0} onClick={save}
        className="w-full bg-sky-700 hover:bg-sky-800 text-white py-2.5 text-xs uppercase tracking-wider font-bold disabled:opacity-50">
        {saving ? "Submitting…" : "Submit Release Request"}
      </button>
    </Modal>
  );
}
